import { StrictMode, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from '@dr.pogodin/react-helmet';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import App from './App';
import { SplashScreen } from '@/shared/components/SplashScreen';
import { getImagePreloadService } from '@/shared/services/ImagePreloadService';
import { installGlobalErrorHandlers, setGameContext } from '@/lib/error-reporter';
import { useGameStore } from '@/features/game/stores/useGameStore';
import './styles/globals.css';

// Install the global error reporter hooks (window.onerror, unhandledrejection,
// flush interval/visibilitychange/pagehide) as early as possible so no crash
// goes unobserved. Attach gameId + turnEpoch from the game store when a game
// is active, so every guard trip's report is traceable to the turn instance
// that tripped it (Story 04-07).
installGlobalErrorHandlers();
useGameStore.subscribe((state) => {
  setGameContext({ gameId: state.gameId ?? undefined, turnEpoch: state.turnEpoch });
});

// Initialize Plausible Analytics
const plausibleScriptUrl = import.meta.env.VITE_PLAUSIBLE_SCRIPT_URL;
if (plausibleScriptUrl) {
  const script = document.createElement('script');
  script.async = true;
  script.src = plausibleScriptUrl;
  document.head.appendChild(script);

  const plausibleFn: PlausibleFunction = (...args: unknown[]) => {
    (plausibleFn.q = plausibleFn.q ?? []).push(args);
  };
  plausibleFn.init = (options?: Record<string, unknown>) => {
    plausibleFn.o = options ?? {};
  };
  window.plausible = window.plausible ?? plausibleFn;
  window.plausible.init?.();
}

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 0
    }
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

/**
 * AppLoader - Manages image preloading and splash screen transition
 *
 * Shows SplashScreen with progress until critical images are loaded,
 * then renders the main App with smooth transition.
 */
function AppLoader() {
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const imageService = getImagePreloadService();

    // If already ready, skip splash screen
    if (imageService.isReady) {
      setIsReady(true);
      setProgress(100);
      return;
    }

    // Subscribe to progress updates
    imageService.onProgress((progressValue) => {
      setProgress(progressValue);
    });

    // Subscribe to ready event
    imageService.onReady(() => {
      setIsReady(true);
      setProgress(100);
    });
  }, []);

  // Show splash screen until images are loaded
  if (!isReady) {
    return <SplashScreen progress={progress} />;
  }

  // Render main app once preloading is complete
  return <App />;
}

createRoot(rootElement).render(
  <StrictMode>
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AppLoader />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </I18nextProvider>
    </HelmetProvider>
  </StrictMode>
);
